import { Helmet } from "react-helmet-async";
import LegalLayout from "@/components/legal/LegalLayout";
import IncidentReportForm from "@/components/incident/IncidentReportForm";

const sections = [
  { id: "purpose", title: "Purpose" },
  { id: "scope", title: "Scope & Definitions" },
  { id: "severity", title: "Severity Levels" },
  { id: "response", title: "Response Process" },
  { id: "roles", title: "Roles & Responsibilities" },
  { id: "communication", title: "Communication" },
  { id: "postmortem", title: "Postmortem & Learning" },
  { id: "report", title: "Report an Incident" },
  { id: "contact", title: "Contact" },
];

const IncidentResponse = () => (
  <>
    <Helmet>
      <title>Incident Response — The Emerging Market</title>
      <meta
        name="description"
        content="How The Emerging Market detects, responds to, communicates about, and learns from security and service incidents."
      />
      <link rel="canonical" href="https://emrkt.co/incident-response" />
    </Helmet>
    <LegalLayout
      title="Incident Response"
      description="How we detect, contain, communicate about, and learn from security and service incidents — and how you can report one."
      lastUpdated="June 16, 2026"
      effectiveDate="June 16, 2026"
      sections={sections}
    >
      <section id="purpose">
        <h2>1. Purpose</h2>
        <p>
          The Emerging Market ("TEM," "we") operates civic-data infrastructure that
          Alabama partners, agencies, funders, and advocates rely on. This plan defines
          how we respond when something goes wrong — whether a security event, a service
          outage, a data-accuracy issue, or abuse — so that impact is contained quickly,
          users are kept informed, and we learn from each incident.
        </p>
        <p>
          It complements our <a href="/security">Security Commitments</a>,{" "}
          <a href="/privacy">Privacy Policy</a>, and{" "}
          <a href="/dpa">Data Processing Agreement</a>.
        </p>
      </section>

      <section id="scope">
        <h2>2. Scope & Definitions</h2>
        <p>An <strong>incident</strong> is any unplanned event that:</p>
        <ul>
          <li>Compromises (or could compromise) the confidentiality, integrity, or availability of TEM systems or user data.</li>
          <li>Degrades the public site, authenticated app, edge functions, or admin tooling.</li>
          <li>Materially misrepresents Alabama legislation, grants, or partner data we publish.</li>
          <li>Involves abuse of TEM by a user, partner, or third party.</li>
        </ul>
        <p>
          A <strong>vulnerability</strong> is a weakness that could lead to an incident if exploited.
          We treat reported vulnerabilities under the same response process at SEV-2 or below until
          severity is confirmed.
        </p>
      </section>

      <section id="severity">
        <h2>3. Severity Levels</h2>
        <p>We classify every incident into one of four levels. The level drives response time, who is paged, and how we communicate.</p>
        <ul>
          <li>
            <strong>SEV-1 — Critical.</strong> Active data exposure, full site outage, or auth compromise.
            Acknowledged within <strong>1 hour</strong>; status page updated within 2 hours; full team
            mobilized.
          </li>
          <li>
            <strong>SEV-2 — High.</strong> Major feature unavailable (e.g., legislation tracker, grant
            finder, auth flow), or confirmed security vulnerability with realistic exploit path.
            Acknowledged within <strong>4 hours</strong>; status page updated within 8 hours.
          </li>
          <li>
            <strong>SEV-3 — Moderate.</strong> Minor feature broken, performance degradation, or
            low-impact security finding. Acknowledged within <strong>1 business day</strong>; resolved or
            scheduled within 5 business days.
          </li>
          <li>
            <strong>SEV-4 — Low.</strong> Cosmetic issues, isolated data inaccuracy, or hardening
            suggestions. Triaged into the normal product backlog.
          </li>
        </ul>
      </section>

      <section id="response">
        <h2>4. Response Process</h2>
        <p>Every incident moves through the same five phases. Phases may overlap for SEV-1.</p>
        <ol>
          <li><strong>Detect.</strong> Triggered by automated monitoring (Sentry, BetterStack uptime, PostHog anomaly), user report via this page, or partner notification.</li>
          <li><strong>Triage.</strong> The on-call responder assigns severity, opens an incident ticket, and notifies the incident commander for SEV-1/SEV-2.</li>
          <li><strong>Contain.</strong> Stop the bleeding — disable affected features, rotate credentials, revoke tokens, isolate data — before deep investigation.</li>
          <li><strong>Eradicate & Recover.</strong> Identify root cause, ship a fix, restore service, and verify the system is healthy.</li>
          <li><strong>Learn.</strong> Within 5 business days of resolution, publish an internal postmortem and (for SEV-1/SEV-2) a public summary.</li>
        </ol>
      </section>

      <section id="roles">
        <h2>5. Roles & Responsibilities</h2>
        <ul>
          <li><strong>Incident Commander.</strong> Owns the response, coordinates the team, makes call-the-shots decisions. Default: founder.</li>
          <li><strong>Technical Lead.</strong> Drives investigation and remediation in code, database, and infrastructure.</li>
          <li><strong>Communications Lead.</strong> Owns status page, customer email, and partner outreach.</li>
          <li><strong>Scribe.</strong> Maintains a real-time timeline of actions, decisions, and findings — feeds the postmortem.</li>
        </ul>
        <p>
          At our current size, one person may wear multiple hats. We document who held each role in the
          postmortem so the seams are visible as we grow.
        </p>
      </section>

      <section id="communication">
        <h2>6. Communication</h2>
        <ul>
          <li><strong>Public status page:</strong> <a href="/status">emrkt.co/status</a> — updated within the timeframes in §3.</li>
          <li><strong>Email notifications:</strong> All affected users receive direct email for SEV-1 and SEV-2 incidents involving data or auth. We don't email for routine SEV-3/SEV-4 issues unless a specific user is impacted.</li>
          <li><strong>Regulatory notification:</strong> Where applicable (e.g., personal data breach), we notify affected users and any relevant authorities within the timeframes required by law (typically 72 hours for GDPR-class events).</li>
          <li><strong>Partner notification:</strong> Agency and funder partners with data-processing agreements receive direct contact for any incident touching their data.</li>
        </ul>
      </section>

      <section id="postmortem">
        <h2>7. Postmortem & Learning</h2>
        <p>
          Every SEV-1 and SEV-2 incident gets a written postmortem within 5 business days of
          resolution. We follow a blameless format covering: timeline, root cause, contributing
          factors, what worked, what didn't, and concrete action items with owners and due dates.
        </p>
        <p>
          We publish redacted SEV-1 summaries on the status page so partners and users can see how we
          responded. Action items are tracked in our infrastructure backlog and reviewed at each
          quarterly planning cycle.
        </p>
      </section>

      <section id="report">
        <h2>8. Report an Incident</h2>
        <p>
          If you've discovered a security issue, data exposure, outage, or anything else that looks
          wrong, please use the form below. For active SEV-1 events you can also email{" "}
          <a href="mailto:security@emrkt.co">security@emrkt.co</a> directly.
        </p>
        <p className="not-prose">
          <IncidentReportForm />
        </p>
        <h3>Responsible Disclosure</h3>
        <p>
          We commit to good-faith security researchers: we will not pursue legal action for research
          conducted in line with our <a href="/acceptable-use">Acceptable Use Policy</a> that avoids
          privacy violations, service disruption, and data destruction. Please give us a reasonable
          window to remediate before public disclosure.
        </p>
      </section>

      <section id="contact">
        <h2>9. Contact</h2>
        <ul>
          <li><strong>Security & incident reports:</strong> <a href="mailto:security@emrkt.co">security@emrkt.co</a></li>
          <li><strong>General inquiries:</strong> <a href="mailto:emergingmrkt@gmail.com">emergingmrkt@gmail.com</a></li>
          <li><strong>Status page:</strong> <a href="/status">emrkt.co/status</a></li>
        </ul>
      </section>
    </LegalLayout>
  </>
);

export default IncidentResponse;